// SPDX-License-Identifier: MIT
// Parse the sovereign endpoint's OpenAI chat-completions response → MagnifyResult.
// The Rust `sovereign_prompt` command returns the raw HTTP body verbatim (mirror
// of `claude_prompt` returning raw CLI stdout) ; this unwraps the OpenAI envelope,
// then hands the assistant message content to the shared fiche extractor.
//
// A sovereign provider is ai-lab's vLLM / LiteLLM endpoint the user *connects*
// (doc 07 §4.A) — Genesis runs no inference of its own.
import type { MagnifyResult } from '../auth/magnify';
import { parseFicheJson, ClaudeBridgeError } from './claude-output';

// OpenAI chat-completions body → the assistant message content string.
export function unwrapSovereignBody(rawBody: string): string {
  let env: Record<string, unknown>;
  try {
    env = JSON.parse(rawBody) as Record<string, unknown>;
  } catch {
    throw new ClaudeBridgeError('sovereign endpoint returned a non-JSON body');
  }
  if (env.error) {
    const e = env.error as Record<string, unknown>;
    throw new ClaudeBridgeError(`sovereign endpoint error: ${String(e.message ?? e.type ?? 'unknown')}`);
  }
  const choices = env.choices;
  if (!Array.isArray(choices) || choices.length === 0) {
    throw new ClaudeBridgeError('sovereign response has no choices');
  }
  const message = (choices[0] as Record<string, unknown>)?.message as Record<string, unknown> | undefined;
  const content = message?.content;
  if (typeof content !== 'string' || content.trim().length === 0) {
    throw new ClaudeBridgeError('sovereign response has no usable message content');
  }
  return content;
}

// Full path: raw HTTP body → MagnifyResult.
export function parseSovereignMagnify(rawBody: string): MagnifyResult {
  return parseFicheJson(unwrapSovereignBody(rawBody));
}
